import React from 'react';
import type { SurveySummaryQuestion } from '../../types/survey.types';

interface RatingResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const RatingResponse: React.FC<RatingResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, type, answered_count, options } = question;
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Calculate average rating
    const totalRating = options.reduce((sum, option) => {
        const rating = parseInt(option.label);
        return sum + (rating * option.count);
    }, 0);
    const averageRating = answered_count > 0 ? (totalRating / answered_count).toFixed(1) : '0.0';
    
    // Sort options by rating (highest to lowest)
    const sortedOptions = [...options].sort((a, b) => parseInt(b.label) - parseInt(a.label));
    
    // Find the maximum count for percentage calculations
    const maxCount = Math.max(...options.map(option => option.count));
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-3">
                    {/* Question Icon */}
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                        {type === 'rating' ? (
                            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.2036 2.55263C10.2402 2.47885 10.2966 2.41674 10.3665 2.37332C10.4365 2.32989 10.5172 2.30688 10.5995 2.30688C10.6818 2.30688 10.7625 2.32989 10.8324 2.37332C10.9024 2.41674 10.9588 2.47885 10.9953 2.55263L12.9203 6.4518C13.0471 6.70844 13.2343 6.93047 13.4658 7.09884C13.6973 7.26721 13.9663 7.37689 14.2495 7.41846L18.5545 8.04846C18.6361 8.06028 18.7127 8.09469 18.7757 8.1478C18.8388 8.2009 18.8857 8.27059 18.9112 8.34897C18.9367 8.42735 18.9397 8.5113 18.92 8.59133C18.9002 8.67135 18.8585 8.74426 18.7995 8.8018L15.6861 11.8335C15.4808 12.0335 15.3272 12.2805 15.2385 12.5532C15.1498 12.8258 15.1287 13.1159 15.177 13.3985L15.912 17.6818C15.9264 17.7633 15.9176 17.8473 15.8866 17.924C15.8556 18.0008 15.8036 18.0673 15.7366 18.116C15.6696 18.1646 15.5903 18.1935 15.5077 18.1992C15.4251 18.205 15.3426 18.1874 15.2695 18.1485L11.4211 16.1251C11.1676 15.992 10.8855 15.9224 10.5991 15.9224C10.3127 15.9224 10.0306 15.992 9.77698 16.1251L5.92948 18.1485C5.85642 18.1871 5.77398 18.2045 5.69152 18.1987C5.60907 18.1928 5.52991 18.1639 5.46306 18.1153C5.39621 18.0667 5.34434 18.0003 5.31336 17.9236C5.28238 17.847 5.27353 17.7632 5.28781 17.6818L6.02198 13.3993C6.07048 13.1166 6.04947 12.8263 5.96076 12.5535C5.87206 12.2807 5.71832 12.0336 5.51281 11.8335L2.39948 8.80263C2.33998 8.74516 2.29781 8.67213 2.27778 8.59186C2.25776 8.51159 2.26068 8.42731 2.28621 8.34862C2.31175 8.26993 2.35887 8.2 2.42221 8.14678C2.48555 8.09357 2.56257 8.05921 2.64448 8.04763L6.94865 7.41846C7.2322 7.37721 7.50147 7.26768 7.7333 7.09929C7.96513 6.93089 8.15257 6.70869 8.27948 6.4518L10.2036 2.55263Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        ) : (
                            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 10.5H18M10.5 3V18M18 3L3 18" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </div>
                    
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {position}. {title}
                            </h3>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <span className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-full border dark:border-gray-600">
                                {type === 'rating' ? 'Rating' : 'Scale'}
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                {answered_count} of {totalResponses} responses
                            </span>
                            <span className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                                {responseRate}% response rate
                            </span>
                        </div>
                    </div>
                </div>
                
                {/* Average Rating */}
                <div className="flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#F59E0B"/>
                    </svg>
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {averageRating} average
                    </span>
                </div>
            </div>

            {/* Rating Breakdown */}
            <div className="space-y-3">
                {sortedOptions.map((option, index) => {
                    const rating = parseInt(option.label);
                    const percentage = answered_count > 0 ? ((option.count / answered_count) * 100).toFixed(0) : '0';
                    const barWidth = answered_count > 0 ? (option.count / maxCount) * 100 : 0;
                    
                    return (
                        <div key={rating} className="flex items-center gap-4">
                            {/* Rating Level */}
                            <div className="flex items-center gap-1 w-12">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {rating}
                                </span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#F59E0B"/>
                                </svg>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="flex-1">
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                                    <div 
                                        className="h-3 rounded-full transition-all duration-300 ease-in-out bg-[#00C49A] dark:bg-emerald-500"
                                        style={{ 
                                            width: `${barWidth}%`
                                        }}
                                    />
                                </div>
                            </div>
                            
                            {/* Count and Percentage */}
                            <div className="flex items-center gap-2">
                                <span className="px-2 py-1 text-sm font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-full">
                                    {option.count}
                                </span>
                                <span className="text-sm text-gray-600 dark:text-gray-300 min-w-[3rem]">
                                    {percentage}%
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RatingResponse;
